import { ref, computed, watch, inject, type Ref, type ComputedRef } from 'vue';

/** MemberDetailLayout에서 provide — 시설별 연월(sessionStorage) 공유 */
export const MEMBER_YEAR_MONTH_KEY = Symbol('memberYearMonth');

const STORAGE_PREFIX = 'caion_member_ym_';

export interface MemberYearMonthContext {
  selectedYear: Ref<number>;
  selectedMonth: Ref<number>;
  /** YYYYMM */
  yearMonth: ComputedRef<string>;
  /** YYYY-MM */
  yearMonthDash: ComputedRef<string>;
  changeMonth: (delta: number) => void;
  setYearMonth: (year: number, month: number) => void;
  onMonthPickerChange: (event: Event) => void;
}

export interface CreateMemberYearMonthOptions {
  /** sessionStorage에 값이 없을 때 현재 월 기준 오프셋 (예: -1 = 전월) */
  defaultMonthOffset?: number;
  /** storage key 접미사 (예: '_wage' → caion_member_ym_{code}_wage) */
  storageKeySuffix?: string;
}

export function getMemberYearMonthStorageKey(memberCode: string, storageKeySuffix = '') {
  return `${STORAGE_PREFIX}${memberCode}${storageKeySuffix}`;
}

function getDefaultYearMonth(monthOffset = 0): { year: number; month: number } {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

function parseStored(raw: string | null): { year: number; month: number } | null {
  if (!raw) return null;
  const parts = raw.split('-');
  if (parts.length < 2) return null;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  if (year >= 2000 && year <= 2100 && month >= 1 && month <= 12) {
    return { year, month };
  }
  return null;
}

export function readMemberYearMonth(memberCode: string, storageKeySuffix = '') {
  if (!memberCode) return null;
  try {
    return parseStored(sessionStorage.getItem(getMemberYearMonthStorageKey(memberCode, storageKeySuffix)));
  } catch {
    return null;
  }
}

export function writeMemberYearMonth(
  memberCode: string,
  year: number,
  month: number,
  storageKeySuffix = ''
) {
  if (!memberCode) return;
  try {
    sessionStorage.setItem(
      getMemberYearMonthStorageKey(memberCode, storageKeySuffix),
      `${year}-${String(month).padStart(2, '0')}`
    );
  } catch {
    /* sessionStorage quota 등 무시 */
  }
}

/** 레이아웃 또는 단독 페이지에서 연월 상태 생성 */
export function createMemberYearMonthState(
  memberCode: Ref<string>,
  options: CreateMemberYearMonthOptions = {}
): MemberYearMonthContext {
  const { defaultMonthOffset = 0, storageKeySuffix = '' } = options;
  const initial = getDefaultYearMonth(defaultMonthOffset);
  const selectedYear = ref(initial.year);
  const selectedMonth = ref(initial.month);

  const applyStored = (code: string) => {
    let stored = readMemberYearMonth(code, storageKeySuffix);
    if (!stored && !storageKeySuffix) {
      try {
        const legacy = localStorage.getItem('caion_bankbook_ym');
        const parsed = parseStored(legacy);
        if (parsed) {
          stored = parsed;
          writeMemberYearMonth(code, parsed.year, parsed.month, storageKeySuffix);
          localStorage.removeItem('caion_bankbook_ym');
        }
      } catch {
        /* ignore */
      }
    }
    if (stored) {
      selectedYear.value = stored.year;
      selectedMonth.value = stored.month;
    }
  };

  watch(
    memberCode,
    (code) => {
      if (code) applyStored(code);
    },
    { immediate: true }
  );

  watch([selectedYear, selectedMonth, memberCode], ([y, m, code]) => {
    if (code) writeMemberYearMonth(code, y, m, storageKeySuffix);
  });

  const setYearMonth = (year: number, month: number) => {
    if (year >= 2000 && year <= 2100 && month >= 1 && month <= 12) {
      selectedYear.value = year;
      selectedMonth.value = month;
    }
  };

  const changeMonth = (delta: number) => {
    let y = selectedYear.value;
    let m = selectedMonth.value + delta;
    if (m < 1) {
      m = 12;
      y -= 1;
    } else if (m > 12) {
      m = 1;
      y += 1;
    }
    setYearMonth(y, m);
  };

  const onMonthPickerChange = (event: Event) => {
    const value = (event.target as HTMLInputElement).value;
    if (!value) return;
    const [year, month] = value.split('-').map(Number);
    if (year) setYearMonth(year, month >= 1 && month <= 12 ? month : selectedMonth.value);
  };

  const yearMonth = computed(
    () => `${selectedYear.value}${String(selectedMonth.value).padStart(2, '0')}`
  );
  const yearMonthDash = computed(
    () => `${selectedYear.value}-${String(selectedMonth.value).padStart(2, '0')}`
  );

  return {
    selectedYear,
    selectedMonth,
    yearMonth,
    yearMonthDash,
    changeMonth,
    setYearMonth,
    onMonthPickerChange,
  };
}

/** 레이아웃 provide 우선, 없으면 memberCode 기준 로컬 상태 */
export function useMemberYearMonthOptional(memberCode: Ref<string>): MemberYearMonthContext {
  const injected = inject<MemberYearMonthContext | null>(MEMBER_YEAR_MONTH_KEY, null);
  if (injected) return injected;
  return createMemberYearMonthState(memberCode);
}

/** 임금설정 페이지 — 전월 기본값, 통장 등과 별도 sessionStorage */
export function useWageMemberYearMonth(memberCode: Ref<string>): MemberYearMonthContext {
  return createMemberYearMonthState(memberCode, {
    defaultMonthOffset: -1,
    storageKeySuffix: '_wage',
  });
}

export function useInjectedMemberYearMonth(): MemberYearMonthContext {
  const ctx = inject<MemberYearMonthContext | null>(MEMBER_YEAR_MONTH_KEY, null);
  if (!ctx) {
    throw new Error('memberYearMonth is not provided. Use within MemberDetailLayout.');
  }
  return ctx;
}
